import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_RECHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 min

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize state from localStorage synchronously to prevent flash
  const [token, setToken] = useState(() => localStorage.getItem("ems_token"));
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem("ems_user");
    return saved ? JSON.parse(saved) : null;
  });
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem("ems_token"));
  const [isLoading, setIsLoading] = useState(false);
  const verificationDone = useRef(false);

  const clearAuthState = useCallback(() => {
    localStorage.removeItem("ems_token");
    localStorage.removeItem("ems_user");
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  const handleSessionExpired = useCallback(() => {
    clearAuthState();
    toast.error("Session expired", {
      description: "Please log in again to continue. Any queued intervention log entries will sync after you log back in.",
      duration: 10000,
      action: {
        label: "Log in",
        onClick: () => { window.location.href = "/login"; },
      },
    });
  }, [clearAuthState]);

  // Verify token in background on mount AND re-verify every 5 min while active
  useEffect(() => {
    let intervalId;
    const verify = (showToastOnFail = false) => {
      const savedToken = localStorage.getItem("ems_token");
      if (!savedToken) {
        setIsAuthenticated(false);
        return Promise.resolve();
      }
      return axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${savedToken}` }
      })
      .then(response => {
        setUser(response.data);
        setToken(savedToken);
        setIsAuthenticated(true);
        localStorage.setItem("ems_user", JSON.stringify(response.data));
      })
      .catch((err) => {
        const status = err?.response?.status;
        // Only treat as expired session on real auth failures (401/403),
        // not on network blips (no response) — keep the user logged in
        // optimistically; another check in 5 min will retry.
        if (status === 401 || status === 403) {
          if (showToastOnFail) {
            handleSessionExpired();
          } else {
            // First-mount: silent logout (user wasn't actively working yet)
            clearAuthState();
          }
        }
        // network error: do nothing — keep current state, retry on next tick
      });
    };

    if (!verificationDone.current) {
      verificationDone.current = true;
      verify(false);
    }

    // Re-verify every 5 min while page is visible
    intervalId = setInterval(() => {
      if (document.visibilityState === "visible" && localStorage.getItem("ems_token")) {
        verify(true);
      }
    }, TOKEN_RECHECK_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [handleSessionExpired, clearAuthState]);

  const login = useCallback((accessToken, userData) => {
    localStorage.setItem("ems_token", accessToken);
    localStorage.setItem("ems_user", JSON.stringify(userData));
    setToken(accessToken);
    setUser(userData);
    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(() => {
    clearAuthState();
  }, [clearAuthState]);

  const userRole = user?.role || "viewer";
  const isAdmin = userRole === "admin";
  const isDemoAdmin = userRole === "demo_admin";
  // Anyone who can navigate the admin UI (real admin OR sandbox/demo)
  const canAdminUI = isAdmin || isDemoAdmin;
  const isEditor = userRole === "editor";

  // Global axios response interceptor — converts SANDBOX_BLOCKED 403s
  // into a friendly toast so demo users see consistent feedback without
  // every component needing its own special-case.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        const detail = error?.response?.data?.detail;
        if (
          error?.response?.status === 403 &&
          typeof detail === "string" &&
          detail.startsWith("SANDBOX_BLOCKED:")
        ) {
          // Show one info toast (not error). De-dupe is handled by sonner id.
          toast("🛟 " + detail.replace("SANDBOX_BLOCKED:", "").trim(), {
            id: "sandbox-blocked",
            duration: 4000,
            description: "Sandbox mode — your change wasn't saved.",
          });
          // Resolve with a "fake success" envelope so component success branches
          // run (re-fetches will show the unchanged data).
          return Promise.resolve({
            data: { _sandbox: true, message: "Sandbox — not persisted" },
            status: 200,
            statusText: "OK (sandbox)",
            headers: {},
            config: error.config,
          });
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  return (
    <AuthContext.Provider value={{ 
      token, 
      user, 
      isAuthenticated, 
      isLoading,
      userRole,
      isAdmin,
      isDemoAdmin,
      canAdminUI,
      isEditor,
      login, 
      logout 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
